import type { BuilderContent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { PrefetchedBuilderContent } from "~/hec/components/prefetched-builder-content";
import { cn } from "~/lib/ui";

import type {
  PlpInlineTileItem,
  TileSpan,
  TileSpanSet,
} from "./interleave-inline-tiles";

// Per-breakpoint Tailwind classes for each span value: a visibility toggle
// (`hidden` / `block`) plus the `col-span-N`. Literal strings so Tailwind's
// content scanner picks them up. A span of "0" hides the node at that
// breakpoint (`display:none`) so it consumes no grid cell — that is what lets
// the interleave algorithm place one authored tile at different cells per
// breakpoint by emitting one DOM node per breakpoint group.
const MOBILE_SPAN: Record<TileSpan, string> = {
  "0": "hidden",
  "1": "block col-span-1",
  "2": "block col-span-2",
  "3": "block col-span-3",
  "4": "block col-span-4",
};

const TABLET_SPAN: Record<TileSpan, string> = {
  "0": "sm:hidden",
  "1": "sm:block sm:col-span-1",
  "2": "sm:block sm:col-span-2",
  "3": "sm:block sm:col-span-3",
  "4": "sm:block sm:col-span-4",
};

const DESKTOP_SPAN: Record<TileSpan, string> = {
  "0": "md:hidden",
  "1": "md:block md:col-span-1",
  "2": "md:block md:col-span-2",
  "3": "md:block md:col-span-3",
  "4": "md:block md:col-span-4",
};

// The FSA Spender grid steps to 3 columns at `lg` (its cart rail takes 360px
// from `md`), so its "desktop" spans start there; see `desktopBreakpoint`.
const LG_DESKTOP_SPAN: Record<TileSpan, string> = {
  "0": "lg:hidden",
  "1": "lg:block lg:col-span-1",
  "2": "lg:block lg:col-span-2",
  "3": "lg:block lg:col-span-3",
  "4": "lg:block lg:col-span-4",
};

const LARGE_DESKTOP_SPAN: Record<TileSpan, string> = {
  "0": "xl:hidden",
  "1": "xl:block xl:col-span-1",
  "2": "xl:block xl:col-span-2",
  "3": "xl:block xl:col-span-3",
  "4": "xl:block xl:col-span-4",
};

/**
 * Renders one PLP Inline Content tile: a `plp-inline-content` section
 * (uniform — embedded HTML lives inside the section as a Custom Code block, so
 * there is no external-source branch). `spans` is the per-node, per-breakpoint
 * visibility/width computed by {@link interleaveInlineTiles}; the wrapper hides
 * itself at breakpoints where span is "0".
 */
export function InlineTileSlot({
  tile,
  spans,
  className,
  desktopBreakpoint = "md",
  as: Wrapper = "div",
}: {
  tile: PlpInlineTileItem;
  spans: TileSpanSet;
  className?: string;
  /** Where the grid's "desktop" columns begin: `md` on PLPs, `lg` on the FSA Spender grid. */
  desktopBreakpoint?: "md" | "lg";
  /** `li` inside a list grid (the FSA Spender grid is a `<ul>`). */
  as?: "div" | "li";
}) {
  const value = tile.content?.value;
  if (!value || Object.keys(value).length === 0) return null;
  const model = tile.content?.model || ModelMap.plpInlineContent;

  // External/absolute-positioned embeds collapse to 0 height when alone in a
  // grid row; an author-supplied aspect-ratio gives the wrapper intrinsic
  // height. (For the legacy external-source path this was auto-extracted from
  // the SVG viewBox; in the section model the author sets it explicitly.)
  const wrapperStyle = tile.aspectRatio
    ? { aspectRatio: tile.aspectRatio }
    : undefined;

  return (
    <Wrapper
      className={cn(
        "relative overflow-hidden",
        MOBILE_SPAN[spans.mobile],
        TABLET_SPAN[spans.tablet],
        (desktopBreakpoint === "lg" ? LG_DESKTOP_SPAN : DESKTOP_SPAN)[
          spans.desktop
        ],
        LARGE_DESKTOP_SPAN[spans.largeDesktop],
        className,
      )}
      style={wrapperStyle}
      data-testid="plp-inline-tile"
      // One authored tile may emit several DOM nodes (one per breakpoint group),
      // all but the active breakpoint's hidden via `display:none`. `data-tile-id`
      // lets QA dedupe the responsive variants down to one logical tile.
      data-tile-id={tile.content?.id}
    >
      <PrefetchedBuilderContent
        model={model}
        content={value as BuilderContent}
      />
    </Wrapper>
  );
}
